import { createFileRoute } from '@tanstack/react-router'
import {
	ClientSideRowModelModule,
	ColDef,
	ModuleRegistry,
	NumberEditorModule,
	TextEditorModule,
	ValidationModule
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useMemo, useState, forwardRef, useImperativeHandle, useRef, useCallback } from 'react'

import { TaxRate } from '@/api/admin-country-types'
import { Price, PriceInput } from '@/api/admin-products-types'
import { SalesChannelListItem } from '@/api/admin-sales-channels-types'
import { useUpdateProductPricing } from '@/api/api-mutations'
import { EndpointResponse } from '@/api/common'
import { Endpoint } from '@/api/common'
import { PricingMethodType, PricingMethod } from '@/api/common-types'
import { useApiContext } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { FocusModal } from '@/components/base/focus-modal'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { useRouteModal } from '@/components/route-modal'
import { RouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { fromPrecision, getCurrencyDecimals, toPrecision } from '@/utils/currency'
import { useAsyncEffect } from '@/utils/hooks'
import { sleep, sortArray } from '@/utils/utilities'

export const Route = createFileRoute('/_authenticated/_general/products/$productUuid/prices')({
	component: ProductPrices
})

function ProductPrices() {
	const { productUuid } = Route.useParams()

	return (
		<RouteModal requiresData loader={async api => await api.getProductPricing({ path: { productUuid } })}>
			{data => <ProductPricesForm data={data} />}
		</RouteModal>
	)
}

function ProductPricesForm({ data }: { data: EndpointResponse<Endpoint.GetProductPricing> }) {
	const { L } = useI18n()
	const { productUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const gridRef = useRef<ProductPricesGridRef>(null)

	const updateProductPricing = useUpdateProductPricing(true)
	const handleSave = useCallback(() => {
		const newPrices = gridRef.current?.getGridData()

		if (!newPrices) {
			return
		}

		updateProductPricing.mutateAsync(
			{
				path: { productUuid },
				body: newPrices
			},
			{
				onSuccess: () => {
					handleSuccess('..')
				},
				onError: e => {
					toast.error(e.message)
				}
			}
		)
	}, [productUuid, handleSuccess, updateProductPricing])

	return (
		<div className='flex size-full flex-col'>
			<FocusModal.Header>
				<FocusModal.Title>
					<Heading>{L.Product.Pricing.Title()}</Heading>
				</FocusModal.Title>
			</FocusModal.Header>
			<FocusModal.Body className='flex flex-col overflow-hidden'>
				<ProductPricesGrid ref={gridRef} initialPrices={data.prices} pricingMethod={data.pricingMethod} />
			</FocusModal.Body>
			<FocusModal.Footer>
				<div className='flex w-full items-center justify-end gap-x-2'>
					<FocusModal.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</FocusModal.Close>
					<Button
						onClick={handleSave}
						variant='primary'
						size='small'
						isLoading={updateProductPricing.isPending}
					>
						{L.General.Save()}
					</Button>
				</div>
			</FocusModal.Footer>
		</div>
	)
}

// Register AG Grid modules
ModuleRegistry.registerModules([ClientSideRowModelModule, NumberEditorModule, TextEditorModule, ValidationModule])

const disabledCellStyle: ColDef<RowData>['cellStyle'] = params =>
	params.data?.enabled ? null : { color: '#888', backgroundColor: '#f5f5f5' }

// Enabled rows first, then alphabetically by sales channel within each group.
// Sort using the active dashboard locale, and fall back to the (unique) sales
// channel uuid when names compare equal (e.g. Unicode-equivalent names).
const sortRows = (rows: RowData[], locale: string): RowData[] =>
	[...rows].sort((a, b) => {
		if (a.enabled !== b.enabled) {
			return a.enabled ? -1 : 1
		}
		const byName = a.salesChannel.localeCompare(b.salesChannel, locale)
		if (byName !== 0) {
			return byName
		}
		return a.salesChannelUuid.localeCompare(b.salesChannelUuid)
	})

export type RowData = {
	enabled: boolean
	salesChannelUuid: string
	salesChannel: string
	currencyCode: string
	price: number | null
	variablePrice: boolean
	taxRate: string
	taxInclusive: boolean
}

export interface ProductPricesGridRef {
	getGridData: () => PriceInput[] | null
}

interface ProductPricesGridProps {
	pricingMethod: PricingMethodType
	initialPrices: Price[]
}

export const ProductPricesGrid = forwardRef<ProductPricesGridRef, ProductPricesGridProps>(
	({ initialPrices, pricingMethod }, ref) => {
		const { L, locale } = useI18n()
		const gridRef = useRef<AgGridReact<any>>(null)
		const [rowData, setRowData] = useState<RowData[]>([])

		const isVariablePrice = useMemo(() => pricingMethod === PricingMethod.PriceEmbedded, [])

		const [taxRates, setTaxRates] = useState<TaxRate[]>([])
		const [salesChannels, setSalesChannels] = useState<SalesChannelListItem[]>([])
		const [loading, setLoading] = useState(false)
		const { api } = useApiContext()

		const isInitializing = useRef(false)
		useAsyncEffect(async () => {
			if (isInitializing.current) {
				return
			}
			isInitializing.current = true
			try {
				setLoading(true)
				const [salesChannelsData, taxRatesData] = await Promise.all([
					api.listSalesChannels({}),
					api.listTaxRates({})
				])
				setTaxRates(sortArray(taxRatesData, x => x.name))
				sortArray(salesChannelsData, x => x.name)
				setSalesChannels(salesChannelsData)
				const data: RowData[] = []
				for (const salesChannel of salesChannelsData) {
					const price = initialPrices.find(p => p.salesChannel.uuid === salesChannel.uuid)
					const taxRate = taxRatesData.find(x => x.uuid === price?.priceInfo?.taxRateUuid)

					data.push({
						enabled: !!price?.priceInfo,
						salesChannelUuid: salesChannel.uuid,
						salesChannel: salesChannel.name,
						currencyCode: salesChannel.currencyCode,
						variablePrice: isVariablePrice,
						price: isVariablePrice
							? null
							: price && !price.priceInfo.variablePrice
								? fromPrecision(price.priceInfo.amount)
								: 0,
						taxRate: taxRate?.name ?? taxRatesData[0].name,
						taxInclusive: price?.priceInfo?.taxInclusive ?? true
					})
				}
				setRowData(sortRows(data, locale))
				setLoading(false)
			} catch (e) {
				console.error(e)
			}
		}, [])

		const colDefs = useMemo<ColDef<RowData>[]>(
			() =>
				[
					{
						field: 'enabled',
						headerName: L.Product.Pricing.Enabled(),
						editable: true,
						cellRenderer: 'agCheckboxCellRenderer',
						cellEditor: 'agCheckboxCellEditor',
						width: 100,
						flex: 0
					},
					{
						field: 'salesChannel',
						headerName: L.Fields.SalesChannel(),
						editable: false
					},
					!isVariablePrice && {
						field: 'price',
						headerName: L.Product.Pricing.Price(),
						editable: true,
						cellEditor: 'agNumberCellEditor',
						cellEditorParams: {
							min: 0,
							precision: 2
						},
						valueFormatter: params =>
							L.Amount.Default({
								amount: params.value ?? 0,
								currencyCode: params.data?.currencyCode ?? ''
							}),
						cellStyle: disabledCellStyle
					},
					{
						field: 'taxRate',
						headerName: L.Fields.TaxRate(),
						cellEditor: 'agSelectCellEditor',
						cellEditorParams: {
							values: taxRates.map(x => x.name)
						},
						cellStyle: disabledCellStyle
					},
					{
						field: 'taxInclusive',
						headerName: L.Fields.TaxInclusive(),
						cellRenderer: 'agCheckboxCellRenderer',
						cellEditor: 'agCheckboxCellEditor',
						cellStyle: disabledCellStyle
					}
				].filter(Boolean) as ColDef<RowData>[],
			[taxRates, L]
		)

		const defaultColDef = useMemo<ColDef>(() => {
			return {
				editable: params => params.data.enabled,
				flex: 1
			}
		}, [])

		useImperativeHandle(ref, () => ({
			getGridData: () => {
				const allRowData: RowData[] = []
				gridRef.current?.api.stopEditing()
				gridRef.current?.api.forEachNode(node => {
					allRowData.push(node.data)
				})

				const newPrices: PriceInput[] = []
				for (const row of allRowData) {
					const taxRate = taxRates.find(x => x.name === row.taxRate)
					if (!taxRate) {
						toast.error(L.Product.Pricing.InvalidTaxRate({ taxRate: row.taxRate }))
						return null
					}
					if (!row.enabled) {
						continue
					}
					newPrices.push({
						salesChannelUuid: row.salesChannelUuid,
						currencyCode: row.currencyCode,
						priceInfo: isVariablePrice
							? {
									variablePrice: true,
									taxRateUuid: taxRate.uuid,
									taxInclusive: row.taxInclusive
								}
							: {
									variablePrice: false,
									amount: row.price
										? toPrecision(row.price, getCurrencyDecimals(row.currencyCode))
										: toPrecision(0),
									taxRateUuid: taxRate.uuid,
									taxInclusive: row.taxInclusive
								}
					})
				}
				return newPrices
			}
		}))

		return (
			<div className='ag-theme-quartz flex-grow'>
				<AgGridReact
					ref={gridRef}
					loading={loading}
					rowData={rowData}
					columnDefs={colDefs}
					defaultColDef={defaultColDef}
					singleClickEdit
					onCellValueChanged={e => {
						if (e.colDef.field === 'price') {
							const enabled = typeof e.newValue === 'number' && e.newValue > 0
							if (e.data.enabled !== enabled) {
								e.node.setDataValue('enabled', enabled)
								return
							}
						}
						if (e.colDef.field === 'enabled') {
							// Create a new array reference to trigger re-render for cellStyle.
							// Rows are only sorted on initial load, not while editing, so a
							// toggled row keeps its position instead of jumping around.
							setRowData(prevRowData => [...prevRowData])
						}
					}}
				/>
			</div>
		)
	}
)

ProductPricesGrid.displayName = 'ProductPricesGrid'
